import { Button, Card, CardBody, CardHeader, type ColumnDef, DataTable, EmptyState, InlineMessage } from '@olytic/foundation';
import { useMemo } from 'react';

import { formatCurrency } from '../objects/format';
import { orderLines, scheduleByMonth, scheduleIsStale, type ScheduleMonth } from './lineTree';
import type { BuilderLine, RevenueScheduleRow } from './quotesApi';

const money = (n: number) => formatCurrency(n);
/** Up to this many commercial lines get a column each; more are summed as one-time and monthly. */
const LINE_COLUMNS_MAX = 4;

/**
 * The quote's revenue schedule: a row per month, a column per commercial line
 * (a bundle counts once, its options inside it), or with more than four of
 * them a one-time and a monthly column, then revenue and hours. Made
 * from the timeline on the Terms tab; Regenerate rebuilds it after the lines or
 * dates change. Hours are internal, as on every quote page.
 */
export function RevenueScheduleCard({
  lines, rows, busy, onGenerate,
}: { lines: BuilderLine[]; rows: RevenueScheduleRow[]; busy: boolean; onGenerate: () => void }): JSX.Element {
  const commercial = useMemo(() => orderLines(lines).filter((l) => !l.parent_line_id), [lines]);
  const months = useMemo(() => scheduleByMonth(rows), [rows]);
  const stale = scheduleIsStale(lines, rows);
  const edited = commercial.filter((l) => l.schedule_edited_at);
  const totals = useMemo(
    () => ({
      revenue: months.reduce((t, m) => t + m.revenue, 0),
      hours: months.some((m) => m.hours !== null) ? months.reduce((t, m) => t + (m.hours ?? 0), 0) : null,
    }),
    [months],
  );

  const columns = useMemo<ColumnDef<ScheduleMonth>[]>(
    () => [
      { key: 'label', label: 'Month', nowrap: true, get: (m) => m.label },
      ...(commercial.length <= LINE_COLUMNS_MAX
        ? commercial.map<ColumnDef<ScheduleMonth>>((line) => ({
            key: `line:${line.id}`,
            label: line.name,
            align: 'right',
            nowrap: true,
            render: (m) => {
              const amount = m.byLine[line.id];
              return amount === undefined ? '—' : money(amount);
            },
          }))
        : (['one_time', 'monthly'] as const).map<ColumnDef<ScheduleMonth>>((billing) => {
            const ids = commercial.filter((l) => l.billing === billing).map((l) => l.id);
            return {
              key: `billing:${billing}`,
              label: billing === 'one_time' ? 'One-time' : 'Monthly',
              align: 'right',
              nowrap: true,
              render: (m) => {
                const amounts = ids.map((lineId) => m.byLine[lineId]).filter((a): a is number => a !== undefined);
                return amounts.length ? money(amounts.reduce((t, a) => t + a, 0)) : '—';
              },
            };
          })),
      { key: 'revenue', label: 'Revenue', align: 'right', nowrap: true, render: (m) => money(m.revenue) },
      { key: 'hours', label: 'Hours', align: 'right', nowrap: true, get: (m) => (m.hours === null ? '—' : String(m.hours)) },
    ],
    [commercial],
  );

  return (
    <Card>
      <CardHeader
        title="Revenue schedule"
        actions={
          commercial.length ? (
            <Button variant={rows.length ? 'neutral' : 'brand'} size="sm" disabled={busy} onClick={onGenerate}>
              {rows.length ? 'Regenerate' : 'Generate from the timeline'}
            </Button>
          ) : null
        }
      />
      {rows.length === 0 ? (
        <CardBody>
          <EmptyState
            title="No schedule yet"
            description="Generate it once the timeline has a kickoff and phases: one-time work spreads across the phases, a license runs for its term."
          />
        </CardBody>
      ) : (
        <>
          {edited.length ? (
            <CardBody className="pb-0">
              <InlineMessage tone="info" role="status">
                {edited.map((l) => l.name).join(', ')} {edited.length === 1 ? 'was' : 'were'} edited by hand on the opportunity and{' '}
                {edited.length === 1 ? 'keeps its' : 'keep their'} months when you regenerate. Revert {edited.length === 1 ? 'it' : 'them'} with Edit schedules there.
              </InlineMessage>
            </CardBody>
          ) : null}
          {stale ? (
            <CardBody className="pb-0">
              <InlineMessage tone="warning" role="status">The lines have changed since this schedule was made. Regenerate it.</InlineMessage>
            </CardBody>
          ) : null}
          <DataTable<ScheduleMonth> columns={columns} rows={months} getRowId={(m) => m.key} density="compact" />
          <CardBody className="flex justify-end gap-6 text-sm">
            <span>Total revenue <strong className="tabular-nums">{money(totals.revenue)}</strong></span>
            {totals.hours !== null ? <span>Total hours <strong className="tabular-nums">{Math.round(totals.hours * 10) / 10}</strong></span> : null}
          </CardBody>
        </>
      )}
    </Card>
  );
}
